import type { ReactNode } from 'react';
import { useContext } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkExclamationIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/exclamation';
import BpkExclamationCircleIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/exclamation-circle';
import classNames from 'classnames';
import { parseISO } from 'date-fns/parseISO';
import reactReplacePlaceholder from 'react-replace-placeholder';

import AirlineIcon from '../AirlineIcon';
import Codeshare from '../Codeshare';
import LocalisationContext from '../Contexts/LocalisationContext';
import StringsContext from '../Contexts/StringsContext';
import { getTranslatedStatus, STATUS } from '../services/translationService';

import type {
  FlightEntryData,
  FlightStatus,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ArrivalsDepartures';

import STYLES from './FlightEntry.module.scss';

export interface FlightEntryDataProps extends FlightEntryData {
  isMobile?: boolean;
  showFlightTrackerLinks?: boolean;
  flightTrackerBasePath?: string;
  useUMSFlightTrackerUrls?: boolean;
}

const BADGE_STATUS: Partial<Record<FlightStatus, keyof typeof BADGE_TYPES>> = {
  CANCELLED: BADGE_TYPES.critical,
  DELAYED: BADGE_TYPES.critical,
  NO_TAKEOFF_INFO: BADGE_TYPES.warning,
};

const BADGE_ICONS: Partial<Record<FlightStatus, ReactNode>> = {
  CANCELLED: <BpkExclamationIcon />,
  DELAYED: <BpkExclamationIcon />,
  NO_TAKEOFF_INFO: <BpkExclamationCircleIcon />,
};

const FlightEntry = ({
  airline,
  airlineImage,
  codeshares = undefined,
  departureTime = undefined,
  estimatedTime = undefined,
  flightCode,
  flightTrackerBasePath = '/flight-tracker',
  flightTrackerUrl = undefined,
  gate = undefined,
  id: _id,
  isMobile = false,
  location,
  showFlightTrackerLinks = false,
  status,
  terminal = undefined,
  time = undefined,
  useUMSFlightTrackerUrls = false,
}: FlightEntryDataProps) => {
  const strings = useContext(StringsContext);
  const localisation = useContext(LocalisationContext);
  const badgeType =
    BADGE_STATUS[status as keyof typeof BADGE_STATUS] || BADGE_TYPES.normal;
  const badgeIcon: ReactNode | undefined =
    BADGE_ICONS[status as keyof typeof BADGE_ICONS] || undefined;

  const isDelayedFlight = status === 'DELAYED' && estimatedTime;
  const formatTime = (value: string) =>
    localisation.formatTime?.(parseISO(value.replace('Z', '')));

  const airlineIconEl = <AirlineIcon airlineImage={airlineImage} />;

  const airlineEl = airline ? (
    <div className={STYLES.FlightEntry__airline}>{airline}</div>
  ) : undefined;

  const flightCodeEl = (
    <div
      className={classNames(STYLES.FlightEntry__flightCode, {
        [STYLES['FlightEntry__flightCode--linked']]: showFlightTrackerLinks,
      })}
    >
      {flightCode}
    </div>
  );

  const locationEl = (
    <div className={STYLES.FlightEntry__location}>{location}</div>
  );

  const timeAndDelayedTimeEl = (
    <div className={STYLES.FlightEntry__time}>
      {time && (
        <div
          className={classNames(STYLES.FlightEntry__scheduledTime, {
            [STYLES['FlightEntry__scheduledTime--delayedFlight']]:
              isDelayedFlight,
          })}
        >
          {formatTime(time)}
        </div>
      )}
      {isDelayedFlight && (
        <div className={STYLES.FlightEntry__estimatedTime}>
          {formatTime(estimatedTime)}
        </div>
      )}
    </div>
  );

  const terminalEl = (
    <div className={STYLES.FlightEntry__terminal}>
      {reactReplacePlaceholder(
        strings.falcon_arrivals_departures_board_flightStatus_terminal_label,
        { terminal: terminal || '-' },
        '{',
        '}',
      )}
    </div>
  );

  const gateEl = gate ? (
    <div className={STYLES.FlightEntry__gate}>
      {reactReplacePlaceholder(
        strings.falcon_arrivals_departures_board_flightStatus_gate_label,
        { gate },
        '{',
        '}',
      )}
    </div>
  ) : undefined;

  const statusEl = (
    <div className={STYLES.FlightEntry__status}>
      {status === 'NO_TAKEOFF_INFO' || status === 'UNKNOWN' ? (
        strings[STATUS[status]]
      ) : (
        <BpkBadge type={badgeType}>
          {badgeIcon}
          {getTranslatedStatus(status, strings)}
        </BpkBadge>
      )}
    </div>
  );
  const codeSharesEl = codeshares ? (
    <div className={STYLES.FlightEntry__codeshares}>
      <Codeshare codeshares={codeshares} />
    </div>
  ) : undefined;

  const cardContents = isMobile ? (
    <div className={STYLES.FlightEntry__wrapperMobile}>
      <div className={STYLES.FlightEntry__topContainer}>
        {airlineIconEl}
        {airlineEl}
        {flightCodeEl}
        <div className={STYLES.FlightEntry__topContainerLeft}>
          {terminalEl}
          {gateEl}
        </div>
      </div>
      <div className={STYLES.FlightEntry__middleContainer}>
        {timeAndDelayedTimeEl}
        <div className={STYLES.FlightEntry__middleContainerBottom}>
          {locationEl}
          {statusEl}
        </div>
      </div>
      {codeSharesEl && (
        <div className={STYLES.FlightEntry__flightAnnotation}>
          {codeSharesEl}
        </div>
      )}
    </div>
  ) : (
    <div className={STYLES.FlightEntry__wrapper}>
      <div className={STYLES.FlightEntry__wrapperInner}>
        <div className={STYLES.FlightEntry__iconAirline}>
          {airlineIconEl}
          {airlineEl}
        </div>
        {flightCodeEl}
        <div className={STYLES.FlightEntry__timeAndTimeDelayed}>
          {timeAndDelayedTimeEl}
        </div>
        {locationEl}
        {statusEl}
        <div className={STYLES.FlightEntry__terminalGate}>
          {terminalEl}
          {gateEl}
        </div>
      </div>
      {codeSharesEl}
    </div>
  );

  const departureSlug = departureTime
    ? `#${departureTime.slice(0, 10)}_${departureTime.slice(11, 16).replace(':', '-')}`
    : '';
  const clientFlightTrackerHref = `${flightTrackerBasePath}/${encodeURIComponent(flightCode.toLowerCase())}${departureSlug}`;
  // Only trust an upstream URL that is a same-origin relative path or an
  // absolute https URL; reject javascript:, data:, protocol-relative, etc.
  const isSafeFlightTrackerUrl = (url: string) =>
    (url.startsWith('/') && !url.startsWith('//')) || /^https:\/\//i.test(url);
  const useUpstreamUrl =
    useUMSFlightTrackerUrls &&
    !!flightTrackerUrl &&
    isSafeFlightTrackerUrl(flightTrackerUrl);

  let flightTrackerHref;
  if (showFlightTrackerLinks) {
    flightTrackerHref = useUpstreamUrl
      ? `${flightTrackerUrl}${departureSlug}`
      : clientFlightTrackerHref;
  }

  return (
    <BpkCard
      className={STYLES.FlightEntry}
      data-testid="flight-entry"
      href={flightTrackerHref}
      atomic={showFlightTrackerLinks ? undefined : false}
    >
      {cardContents}
    </BpkCard>
  );
};

export default FlightEntry;
